import React, { type ReactNode } from 'react';
import { TextField as AriaTextField, type TextFieldProps as AriaTextFieldProps, composeRenderProps } from 'react-aria-components';
import { FieldGroup } from '../base-controls/FieldGroup';
import { cn } from '../../../components/utils';
import type { BaseFieldProps } from '../../../components/types';
import { FieldLabelWithHtml } from '../base-controls/FieldLabelWithHtml';
import { FieldDescription } from '../base-controls/FieldDescription';
import { FieldError } from '../base-controls/FieldError';
import { TextInput, type TextInputProps } from '../base-controls/text-controls/TextInput';
import { baseControlStyles } from '../base-controls/util';

export interface TextFieldProps extends AriaTextFieldProps, BaseFieldProps {
	inputProps?: TextInputProps;
	customInput?: ReactNode;
	children?: ReactNode;
}

export const TextField = React.memo(function TextFieldComponent(props: TextFieldProps) {
	const { label, description, inputProps, children = null, customInput } = props;
	const { base } = baseControlStyles();
	return (
		<AriaTextField
			{...props}
			value={props.value || ''}
			className={composeRenderProps(props.className, className => cn(base(), className))}
			aria-label={label}
			isRequired={props.required}
		>
			{!props.hideLabel ? <FieldLabelWithHtml className="mb-1" label={props.label} required={props.required} /> : null}
			{!children && !customInput ? (
				<TextInput {...inputProps} readOnly={props.isReadOnly} />
			) : customInput ? (
				<> {customInput}</>
			) : (
				<>
					<FieldGroup className="w-auto min-w-[208px]">{children}</FieldGroup>
					<FieldGroup className="w-auto min-w-[208px]">
						<TextInput {...inputProps} readOnly={props.isReadOnly} className="border-0 outline-none" />
						{children}
					</FieldGroup>
				</>
			)}

			{description && <FieldDescription>{description}</FieldDescription>}

			{props.error && <FieldError {...props.error} />}
		</AriaTextField>
	);
});
